import { useEffect, useRef, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { EditUserForm } from './EditUserForm';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useUpdateUserMutation } from 'src/services/usersService';
import {
  baseApi,
  useAddProjectTeamMembersMutation,
  useDeleteProjectTeamMembersMutation,
} from 'src/services';
import { ProductsTypeBack } from 'src/constants/products';
import { useAppDispatch } from 'src/store';
import { Button } from 'src/components/ui/button';
import type { User } from 'src/services/usersService';
import type { EditUserSubmitData } from './EditUserForm';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const EditUserPopup = ({
  user,
  disabled = false,
  onOpenChange,
}: {
  user: User;
  disabled?: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const contentRef = useRef(null);
  const [open, setOpen] = useState(false);
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const [
    updateUserInfoById,
    { isSuccess: isSuccessUpdateUserInfoById, isLoading: isLoadingUpdateUserInfoById },
  ] = useUpdateUserMutation();

  const [addTeamMember, { isLoading: isLoadingAddTeamMember, isSuccess: isSuccessAddTeamMember }] =
    useAddProjectTeamMembersMutation();

  const [
    deleteTeamMember,
    { isSuccess: isSuccessDeleteTeamMember, isLoading: isLoadingDeleteTeamMember },
  ] = useDeleteProjectTeamMembersMutation();

  useEffect(() => {
    if (isSuccessUpdateUserInfoById) {
      notification.success('User Updated.');
    }
  }, [isSuccessUpdateUserInfoById]);

  useEffect(() => {
    if (isSuccessDeleteTeamMember) {
      notification.success('Team Member Deleted.');
    }
  }, [isSuccessDeleteTeamMember]);

  useEffect(() => {
    if (isSuccessAddTeamMember) {
      notification.success(`Team Member was added.`);
    }
  }, [isSuccessAddTeamMember]);

  useLoaderWhile(isLoadingDeleteTeamMember, isLoadingUpdateUserInfoById, isLoadingAddTeamMember);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    contentRef?.current && onOpenChange?.(open);

    if (open) {
      timeout = setTimeout(() => {
        (document.activeElement as HTMLElement)?.blur();
      }, 0);
    }

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [open]);

  const submit = (formData: EditUserSubmitData) => {
    const promises = [];
    const { selectedProjects, ...data } = formData;
    const projectsToAdd = selectedProjects.filter((selectedProject) => {
      return (
        user.projects && !user.projects.some((userProject) => userProject.id === selectedProject.id)
      );
    });
    const projectsToDelete = user.projects?.filter((userProject) => {
      return !selectedProjects.some((selectedProject) => userProject.id === selectedProject.id);
    });

    if (Object.keys(data).length) {
      const updateUserInfoPromise = updateUserInfoById({ userId: user.id, body: data });

      promises.push(updateUserInfoPromise);
    }

    if (projectsToAdd?.length) {
      for (const { id, project_type } of projectsToAdd) {
        const addTeamMemberPromise = addTeamMember({
          id,
          type: ProductsTypeBack[project_type],
          users: [user.id],
        });

        promises.push(addTeamMemberPromise);
      }
    }

    if (projectsToDelete?.length) {
      for (const { id, project_type, team_member_id } of projectsToDelete) {
        if (team_member_id) {
          const deleteTeamMemberPromise = deleteTeamMember({
            id,
            type: ProductsTypeBack[project_type],
            teamMemberId: team_member_id,
          });

          promises.push(deleteTeamMemberPromise);
        }
      }
    }

    Promise.all(promises)
      .then(() => dispatch(baseApi.util.invalidateTags(['UsersAll'])))
      .then(() => setOpen(false));
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'outline-0 cursor-pointer')}
      >
        <button className={'user-settings-button'}>Edit</button>
      </DialogTrigger>
      <DialogContent ref={contentRef}>
        <DialogHeader>
          <DialogTitle>Edit user information</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {user && <EditUserForm user={user} onSubmit={(formData) => submit(formData)} />}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="pill-blue"
            size="pill"
            style={{ width: 'auto' }}
            type="submit"
            form="edit-user-form"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
